import { BackgroundPageDelivery } from './backgroundPageDelivery'
import { getPageYdoc, hasPageYdoc, releasePageYdoc } from '../pages/pageYdocRegistry'
import { withRequestDeadline, throwIfAborted } from './requestDeadline'
import { journalMutation, clearJournalMutation, readJournalMutations } from './mutationRecovery'
/**
 * syncEngine.ts — Cross-Platform Client Sync Engine for Superelectric Ink.
 *
 * Implements:
 *   • Encrypted Yjs Document Sync (via Durable Objects & EncryptedYjsProvider)
 *   • Document Tree Sync (REST + LWW conflict resolution via HLC)
 *   • User Settings Sync (REST + Per-Key LWW via HLC)
 *   • Offline Outbox queuing and automatic reconnection flushing
 *   • Initial local data E2EE migration to Cloudflare edge (D1 / R2)
 *   • Zero-knowledge guarantees across all synchronization paths
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import * as Y from 'yjs'
import { localSaveFailed, localSaveSucceeded, serializeLocalWrite } from '../pages/localSaveState'
import { db, type StoredMutation } from '../platform/webStorage'
import { localHLC, isPlausibleHlc } from './hlc'
import type { DocumentMetadata } from '../pages/documentMetadata'
import { keyManager } from '../crypto/keyManager'
import {
  aesGcmEncrypt,
  aesGcmDecrypt,
  importAesKey,
} from '../crypto/crypto'
import { EncryptedYjsProvider, type ConnectionStatus, type DeliveryStatus } from './encryptedYjsProvider'
export type { ConnectionStatus, DeliveryStatus } from './encryptedYjsProvider'
import { countWordsFromContent } from '../../utils/contentUtils'
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror'
import {
  clearLocalYjsState,
  hasStoredYjsState,
  listDormantPagesWithEdits,
  readLocalYjsState,
  summarizeDormantPageEdits,
  LocalYjsDocument,
  loadLocalYjsOutbox,
  YJS_OUTBOX_UPDATED_EVENT,
} from '../pages/yjsLocalPersistence'
import {
  SyncRequestError,
  classifyFailure,
  worthSplitting,
  afterFailure,
  isDue,
  supersedes,
  isStale,
  isRetiredPageText,
  BLOCKED_RETRY_DELAYS_MS,
} from './outboxPolicy'
import { syncLog } from './syncLog'
import { summarizeSync, type SyncSummary } from './syncSummary'
import { tiptapToPlainText } from './syncHub'
import { TreeCursorStore, getPageCatchUpState, getSettingsCursor, setPageCatchUpState, setSettingsCursor } from './syncCursors'
import { decryptSettingValue, decryptTreeNode, encryptSettingChange, encryptTreeChange } from './syncPayloads'
import { decodeServerVersions, decryptSnapshotBlob, localVersionList, packSnapshotBlob, reconcileWithLocalVersions, type ServerVersionRow } from './versionHistory'
import { describeMutation, groupOutboxItems, logSent } from './outboxBatch'
export type { SyncSummary } from './syncSummary'

/** How far back sending speed is measured. */
const RATE_WINDOW_MS = 120_000

export interface SyncEngineConfig {
  apiUrl?: string
  wsUrl?: string
  authToken?: string | null
  workspaceId?: string | null
  userId?: string | null
}

type AuthRefreshHandler = () => Promise<string | null>

export type SyncState = 'synced' | 'pending' | 'offline' | 'stopped' | 'lapsed'

const SYNC_STOPPED_KEY = 'ink-sync-stopped'

export interface TreeChange {
  id: string
  /**
   * Tree mutations are whole-row LWW updates on the server. Every caller must
   * therefore carry the node's current parent, even when changing only its
   * title, icon, color, or metadata. Omitting it would be serialized as null
   * and move the node to the workspace root.
   */
  parentId: string | null
  title?: string
  titleEncrypted?: string | null
  titleNonce?: string | null
  type?: 'folder' | 'document'
  subtype?: 'desk' | 'folder' | 'page'
  icon?: string | null
  color?: string | null
  iconEncrypted?: string | null
  iconNonce?: string | null
  colorEncrypted?: string | null
  colorNonce?: string | null
  metadata?: DocumentMetadata
  metadataEncrypted?: string | null
  metadataNonce?: string | null
  position: number
  hlcTimestamp?: number
  deleted?: boolean
}

export interface SettingChange {
  key: string
  value: any
  hlc?: number
}

/** What the cloud holds for a page; see the worker's document-status route. */
/** A page's edit-log head as recorded on the server (see worker page_heads). */
export interface PageHead {
  id: string
  headSeq: number
  logId: string
}

export interface PageCatchUpState {
  cursor: number | null
  deferred: string[]
}

export interface DocumentServerStatus {
  id: string
  state: 'active' | 'trashed' | 'purged' | 'missing'
  hasKey: boolean
}

export interface LocalDocumentRecovery {
  hadLocalState: boolean
  available: boolean
}

/** Hidden key on a pullSettings() result; kept off the enumerable setting keys. */
const SETTINGS_PULL_CURSOR = Symbol('settingsPullCursor')

const DEFAULT_API_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || 'https://api.superelectric.ink'

/**
 * Records a pull that carried timestamps from a broken clock. localHLC
 * ignores them (see isPlausibleHlc), and the server re-stamps such writes;
 * an entry that keeps returning means something is still producing them.
 */
function noteImplausibleHlcs(targetType: 'tree' | 'setting', ids: string[]): void {
  if (ids.length === 0) return
  syncLog.record({
    direction: 'in',
    level: 'warn',
    kind: 'clock-skew',
    message: `Ignored ${ids.length} timestamp${ids.length === 1 ? '' : 's'} from a clock far in the future`,
    targetType,
    targetIds: ids,
    items: ids.length,
  })
}

export class SyncEngine {
  private backgroundDelivery: BackgroundPageDelivery
  private apiUrl: string
  private wsUrl: string
  private _authToken: Ref<string | null> = ref(null)
  private _workspaceId: Ref<string | null> = ref(null)
  private _userId: Ref<string | null> = ref(null)
  private authRefreshHandler: AuthRefreshHandler | null = null
  private authRefreshPromise: Promise<string | null> | null = null

  /**
   * The token every cloud request uses. While sync is stopped, or the sync
   * subscription has lapsed, it reads as null, so no document, tree or
   * settings traffic leaves this device; the account session itself (held by
   * the auth store) stays signed in.
   */
  public get authToken(): string | null {
    return this.syncStopped.value || this.syncLapsed.value ? null : this._authToken.value
  }
  public set authToken(val: string | null) {
    this._authToken.value = val
  }

  public get workspaceId(): string | null {
    return this._workspaceId.value
  }
  public set workspaceId(val: string | null) {
    this._workspaceId.value = val
  }

  public get userId(): string | null {
    return this._userId.value
  }
  public set userId(val: string | null) {
    this._userId.value = val
  }

  public isOnline: Ref<boolean> = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
  /** Per-device choice to stop cloud sync while staying signed in. */
  public syncStopped: Ref<boolean> = ref(readSyncStopped())
  /** The account's sync subscription has ended; the app still works locally. */
  public syncLapsed: Ref<boolean> = ref(false)
  /** The sync end date from this device's license certificate, for display. */
  public syncPaidUntil: Ref<string | null> = ref(null)
  public isKeyUnlocked: Ref<boolean> = ref(keyManager.isUnlocked())
  public isSyncing: Ref<boolean> = ref(false)
  /** Outbox items not yet accepted by the server, including failed ones. */
  public pendingCount: Ref<number> = ref(0)
  /** Outbox items the server refused, waiting for the user. */
  public failedCount: Ref<number> = ref(0)
  /** Pages holding journaled edits that have not reached the cloud. */
  public pendingPageCount: Ref<number> = ref(0)
  /** Last time an outbox item reached the server. */
  public lastSentAt: Ref<number | null> = ref(null)
  /** When the waiting count last rose from zero. */
  public waitingSince: Ref<number | null> = ref(null)
  /** Most items waiting at once since the queue was last empty. */
  public backlogTotal: Ref<number> = ref(0)
  /** When the next automatic flush will run, if one is scheduled. */
  public nextRetryAt: Ref<number | null> = ref(null)
  /** The last pass could not reach the server at all. */
  public serverUnreachable: Ref<boolean> = ref(false)
  /** Last time this device checked the server for others' changes. */
  public lastCheckedAt: Ref<number | null> = ref(null)
  /** A tree pull has been applied to the corpus since launch. */
  public treePulled: Ref<boolean> = ref(false)
  /** Ticks every second while anything is waiting, so time-based states update. */
  private clock: Ref<number> = ref(Date.now())
  private clockTimer: ReturnType<typeof setInterval> | null = null
  /** Recent sends, for the sending rate. */
  private sendTimes: Ref<Array<{ at: number; items: number }>> = ref([])
  /** Previous connection status per page, to log only real changes. */
  private loggedPageStatus = new Map<string, ConnectionStatus>()
  /** Removes each connected page's listener for incoming edits. */
  private remoteEditListeners = new Map<string, () => void>()
  public activeDocumentStatus: Ref<ConnectionStatus> = ref('disconnected')
  public activeDocumentDeliveryStatus: Ref<DeliveryStatus> = ref('synced')
  public documentStatuses: Ref<Record<string, ConnectionStatus>> = ref({})
  private currentActiveDocId: string | null = null

  private activeProviders: Map<string, EncryptedYjsProvider> = new Map()
  private providerLingerTimers: Map<string, ReturnType<typeof setTimeout>> = new Map()
  private localDocuments = new WeakMap<Y.Doc, Promise<LocalYjsDocument | null>>()
  public newDocuments = new Set<string>()

  /** Map of in-flight document connection promises to deduplicate concurrent connectDocument calls */
  private pendingConnections: Map<string, Promise<EncryptedYjsProvider | null>> = new Map()
  /** Set of document IDs whose in-flight connections were canceled via disconnectDocument */
  private canceledConnections: Set<string> = new Set()

  /** In-flight flush promise to deduplicate concurrent flush calls within the same tab */
  private flushPromise: Promise<void> | null = null
  /** Next automatic flush, while anything is waiting. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  /** Passes in a row that could not reach the server. */
  private blockedPasses = 0
  /** BroadcastChannel for cross-tab sync status coordination */
  private syncChannel: BroadcastChannel | null = null

  /** Tree cursors per workspace (see syncCursors.ts). */
  private treeCursors = new TreeCursorStore()

      /**
       * Document IDs whose locally-generated DEK has NOT been confirmed accepted
       * by the server yet. A doc can sit here indefinitely if its very first key
       * upload fails (offline blip, cold worker, etc) — keyManager caches the DEK
       * locally regardless, so `getDocumentKey()` returning non-null must never be
       * treated as "the server has it too". Persisted so a failed upload is
       * retried on future app launches, not just later in the same session.
       */
      private static readonly STORAGE_KEY_PENDING_UPLOADS = 'ink-pending-key-uploads'
      private pendingKeyUploads: Set<string> = this.loadPendingKeyUploads()

        private loadPendingKeyUploads(): Set<string> {
          if (typeof localStorage === 'undefined') return new Set()
            try {
              const raw = localStorage.getItem(SyncEngine.STORAGE_KEY_PENDING_UPLOADS)
              if (!raw) return new Set()
                const arr = JSON.parse(raw)
                return Array.isArray(arr) ? new Set(arr) : new Set()
            } catch {
              return new Set()
            }
        }

        private savePendingKeyUploads(): void {
          if (typeof localStorage === 'undefined') return
            try {
              localStorage.setItem(
                SyncEngine.STORAGE_KEY_PENDING_UPLOADS,
                JSON.stringify([...this.pendingKeyUploads])
              )
            } catch {}
        }

        private markKeyUploadPending(docId: string): void {
          this.pendingKeyUploads.add(docId)
          this.savePendingKeyUploads()
        }

        private clearKeyUploadPending(docId: string): void {
          if (this.pendingKeyUploads.delete(docId)) {
            this.savePendingKeyUploads()
          }
        }

        /**
         * Retries uploading any locally-cached DEK the server never confirmed
         * receiving. Safe to call opportunistically (e.g. whenever sync becomes
         * available) — each entry is only cleared once the server actually
         * acknowledges it.
         */
        private keyUploadPass: Promise<void> | null = null
        public retryPendingKeyUploads(): Promise<void> {
          if (!this.keyUploadPass) this.keyUploadPass = this.uploadPendingKeys().finally(() => { this.keyUploadPass = null })
          return this.keyUploadPass
        }
        private async uploadPendingKeys(): Promise<void> {
          if (!this.canSync.value || this.pendingKeyUploads.size === 0) return
            for (const docId of [...this.pendingKeyUploads]) {
              const dek = keyManager.getDocumentKey(docId)
              if (!dek) {
                // Retain the pending marker: replacing a missing key could strand local writing.
                continue
              }
              try {
                const wrapped = await keyManager.getOrGenerateAndWrapDek(docId, keyManager.getDocumentEpoch(docId))
                const response = await this.request<{ wrappedKey?: string; wrappedKeyNonce?: string }>(`/api/documents/${docId}/keys`, 'POST', {
                  targetUserId: this.userId || undefined,
                  wrappedKey: wrapped.ciphertext,
                  wrappedKeyNonce: wrapped.iv,
                  epoch: keyManager.getDocumentEpoch(docId) ?? 1,
                })
                if (response?.wrappedKey && response.wrappedKey !== wrapped.ciphertext) {
                  await this.adoptDocumentKey(docId, { ciphertext: response.wrappedKey, iv: response.wrappedKeyNonce! }, keyManager.getDocumentEpoch(docId) ?? 1)
                }
                this.clearKeyUploadPending(docId)
                console.log(`[SyncEngine] Recovered pending key upload for ${docId}`)
              } catch (e) {
                console.warn(`[SyncEngine] Retry of pending key upload for ${docId} still failing:`, e)
              }
            }
        }

  /**
   * Registers pages created by a corpus import. Their DEKs were generated
   * locally; the uploads are recorded as pending (so they survive a reload)
   * and attempted immediately while sync is available.
   */
  public async registerImportedDocuments(docIds: string[]): Promise<void> {
    for (const docId of docIds) {
      this.newDocuments.add(docId)
      this.pendingKeyUploads.add(docId)
    }
    this.savePendingKeyUploads()
    await this.retryPendingKeyUploads()
  }

  /**
   * The last tree change number this device has applied for a workspace, or
   * null when it has never completed a pull (which makes the next pull full).
   */
  public getTreeCursor(workspaceId: string): number | null {
    return this.treeCursors.get(workspaceId)
  }

  /** Stores the cursor returned by the server with a tree pull. */
  public setTreeCursor(workspaceId: string, cursor: number): void {
    this.treeCursors.set(workspaceId, cursor)
  }

  /**
   * Clears the tree cursor for a specific workspace, or, if omitted, every
   * tree and settings cursor (used on sign-out).
   */
  public clearTreeCursor(workspaceId?: string): void {
    if (workspaceId) this.treeCursors.clear(workspaceId)
    else this.treeCursors.clearAll()
  }

  /**
   * What page catch-up has handled in a workspace: the page-heads cursor (null
   * before the first complete pass) and pages it skipped while they were in
   * the Trash, to catch up if they come back.
   */
  public getPageCatchUpState(workspaceId: string): PageCatchUpState {
    return getPageCatchUpState(workspaceId)
  }

  public setPageCatchUpState(workspaceId: string, state: PageCatchUpState): void {
    setPageCatchUpState(workspaceId, state)
  }

  /** Pages whose edit logs moved after the cursor (every page when null), with each log's newest position. */
  public async pullPageHeads(workspaceId: string, after: number | null): Promise<{ heads: PageHead[]; cursor: number }> {
    const url = after !== null
      ? `/api/workspaces/${workspaceId}/page-heads?after=${after}`
      : `/api/workspaces/${workspaceId}/page-heads`
    try {
      const res = await this.request<{ heads?: PageHead[]; cursor?: number }>(url)
      return { heads: Array.isArray(res.heads) ? res.heads : [], cursor: Number(res.cursor) || 0 }
    } catch (err) {
      syncLog.record({ direction: 'in', level: 'warn', kind: 'page-heads-pull', message: `Couldn’t check which pages changed: ${errorText(err)}`, httpStatus: statusOf(err) })
      throw err
    }
  }

  /**
   * Brings a closed page's local copy up to date from its edit log, without
   * an editor, and returns its document. Never creates a key: a page whose key
   * the server doesn't have yet fails here and is retried on a later pass.
   * `inUse` says whether the app has started using the page meanwhile, in
   * which case its connection and Y.Doc are left to the app.
   */
  public async catchUpPage(docId: string, signal: AbortSignal, inUse: () => boolean): Promise<unknown> {
    throwIfAborted(signal)
    if (!keyManager.getDocumentKey(docId)) {
      const keyRow = await this.request<{ wrapped_key: string; wrapped_key_nonce: string; epoch: number }>(`/api/documents/${docId}/keys`)
      await this.adoptDocumentKey(docId, { ciphertext: keyRow.wrapped_key, iv: keyRow.wrapped_key_nonce }, keyRow.epoch)
      throwIfAborted(signal)
    }
    const hadYdoc = hasPageYdoc(docId)
    const ydoc = getPageYdoc(docId)
    const read = () => yXmlFragmentToProsemirrorJSON(ydoc.getXmlFragment('default'))
    // A live connection (the open page, or one still warm after leaving it) is already current.
    if (this.hasDocumentConnection(docId)) return read()

    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      if (inUse() || this.currentActiveDocId === docId) return
      this.disconnectDocument(docId)
      // Only a Y.Doc this pass created is let go; anything older belongs to the app.
      if (hadYdoc || inUse()) return
      releasePageYdoc(docId)
      void this.releaseLocalDocument(ydoc).finally(() => ydoc.destroy())
    }
    signal.addEventListener('abort', finish, { once: true })
    try {
      const provider = await this.connectDocument(docId, ydoc, { background: true })
      throwIfAborted(signal)
      if (!provider) throw new Error('Cloud connection is unavailable')
      await withRequestDeadline(() => provider.waitForInitialSync(), { signal, timeoutMs: 30_000 })
      await (await this.localDocuments.get(ydoc))?.persistCacheNow()
      return read()
    } finally {
      signal.removeEventListener('abort', finish)
      finish()
    }
  }

  /** Single derived source of truth: network is up, auth token exists, and key session is unlocked. */
  public canSync: ComputedRef<boolean> = computed(() => {
    return this.isOnline.value && Boolean(this.authToken) && this.isKeyUnlocked.value
  })

  /** Items sent per minute over the last two minutes of sending. */
  public ratePerMinute: ComputedRef<number> = computed(() => {
    const now = this.clock.value
    const recent = this.sendTimes.value.filter((send) => now - send.at <= RATE_WINDOW_MS)
    if (recent.length === 0) return 0
    const items = recent.reduce((sum, send) => sum + send.items, 0)
    const since = Math.max(now - RATE_WINDOW_MS, this.waitingSince.value ?? 0)
    const minutes = Math.max(now - since, 10_000) / 60_000
    return items / minutes
  })

  /** What the sync status label says while signed in and online. */
  public syncSummary: ComputedRef<SyncSummary> = computed(() => summarizeSync({
    now: this.clock.value,
    waiting: Math.max(0, this.pendingCount.value + this.pendingPageCount.value - this.failedCount.value),
    failed: this.failedCount.value,
    pages: 0,
    isSyncing: this.isSyncing.value,
    serverUnreachable: this.serverUnreachable.value,
    lastSentAt: this.lastSentAt.value,
    waitingSince: this.waitingSince.value,
    backlogTotal: this.backlogTotal.value,
    ratePerMinute: this.ratePerMinute.value,
    nextRetryAt: this.nextRetryAt.value,
  }))

  public syncStatus: ComputedRef<SyncState> = computed(() => {
    if (this.syncLapsed.value) return 'lapsed'
    if (this.syncStopped.value) return 'stopped'
    if (!this.isOnline.value || !this._authToken.value) return 'offline'
    if (this.isSyncing.value || this.pendingCount.value > 0 || this.pendingPageCount.value > 0) return 'pending'
    return 'synced'
  })

  constructor(config?: SyncEngineConfig) {
    this.apiUrl = (config?.apiUrl || DEFAULT_API_URL).replace(/\/$/, '')
    this.wsUrl = config?.wsUrl || this.apiUrl.replace(/^http/, 'ws')
    this.authToken = config?.authToken ?? null
    this.workspaceId = config?.workspaceId ?? null
    this.userId = config?.userId ?? null

    this.backgroundDelivery = new BackgroundPageDelivery({
      ready: () => this.canSync.value,
      prepare: async signal => { await this.flushOutbox(); throwIfAborted(signal); await this.retryPendingKeyUploads() },
      send: (docId, signal) => this.sendQueuedPage(docId, signal),
      changed: () => this.refreshPendingCount(),
    })

    // Sync keyManager unlock events with reactive state
    keyManager.onUnlockChange((unlocked) => {
      this.isKeyUnlocked.value = unlocked
    })

    // Listen for outbox changes from other tabs via BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.syncChannel = new BroadcastChannel('superelectric-ink-sync')
        this.syncChannel.onmessage = (event) => {
          if (event.data?.type === 'outbox-updated' || event.data?.type === 'yjs-outbox-updated') {
            void this.refreshPendingCount()
            if (event.data?.type === 'yjs-outbox-updated') void this.backgroundDelivery.nudge()
          }
        }
      } catch {}
    }

    // Consolidate all flush triggers into a single readiness change hook:
    // Triggers on: network reconnect, JWT refresh/login, or key restore/unlock.
    watch(
      () => this.canSync.value,
      (ready, wasReady) => {
        if (ready && !wasReady) {
          // Time spent unable to sync is not a stall.
          if (this.waitingSince.value !== null) this.waitingSince.value = Date.now()
          void this.backgroundDelivery.nudge()
        } else if (!ready) this.backgroundDelivery.stop()
      },
      { immediate: true }
    )

    if (typeof window !== 'undefined') {
      window.addEventListener(YJS_OUTBOX_UPDATED_EVENT, () => {
        void this.refreshPendingCount()
        void this.backgroundDelivery.nudge()
        this.syncChannel?.postMessage({ type: 'yjs-outbox-updated' })
      })
      window.addEventListener('online', () => {
        this.isOnline.value = true
        syncLog.record({ direction: 'system', kind: 'network', message: 'This device is back online' })
      })
      window.addEventListener('offline', () => {
        this.isOnline.value = false
        syncLog.record({ direction: 'system', level: 'warn', kind: 'network', message: 'This device went offline' })
      })
      window.addEventListener('focus', () => {
        void this.backgroundDelivery.nudge()
        this.refreshPendingCount()
      })
      void this.recoverPendingMutations().then(() => this.compactOutbox()).finally(async () => { await this.refreshPendingCount(); void this.backgroundDelivery.nudge() })
    }
  }

  public init(config: SyncEngineConfig): void {
    if (config.apiUrl) this.apiUrl = config.apiUrl.replace(/\/$/, '')
    if (config.wsUrl) this.wsUrl = config.wsUrl
    else this.wsUrl = this.apiUrl.replace(/^http/, 'ws')

    if (config.authToken !== undefined) this.authToken = config.authToken
    if (config.workspaceId !== undefined) this.workspaceId = config.workspaceId
    if (config.userId !== undefined) this.userId = config.userId
  }

  public setAuthToken(token: string | null): void {
    this.authToken = token
  }

  public setWorkspaceId(workspaceId: string | null): void {
    this.workspaceId = workspaceId
  }

  public setUserId(userId: string | null): void {
    this.userId = userId
  }

  /**
   * Installs the auth store's refresh callback without importing the store here
   * (which would create a syncEngine <-> authStore circular dependency).
   * Concurrent 401s share one refresh operation.
   */
  public setAuthRefreshHandler(handler: AuthRefreshHandler | null): void {
    this.authRefreshHandler = handler
  }

  private async refreshAuthToken(): Promise<string | null> {
    if (!this.authRefreshHandler) return null
    if (this.authRefreshPromise) return this.authRefreshPromise

    this.authRefreshPromise = this.authRefreshHandler()
    try {
      const token = await this.authRefreshPromise
      if (token) this.authToken = token
      return token
    } finally {
      this.authRefreshPromise = null
    }
  }

  /**
   * Stops or resumes cloud sync on this device. Stopping closes every live
   * document connection; local edits keep queueing in the outbox and upload
   * once sync resumes.
   */
  public setSyncStopped(stopped: boolean): void {
    if (stopped !== this.syncStopped.value) {
      syncLog.record({ direction: 'system', kind: 'sync-stopped', message: stopped ? 'Sync stopped on this device' : 'Sync resumed on this device' })
    }
    this.syncStopped.value = stopped
    try {
      if (stopped) localStorage.setItem(SYNC_STOPPED_KEY, '1')
      else localStorage.removeItem(SYNC_STOPPED_KEY)
    } catch {}
    if (stopped) this.disconnectAllDocuments()
  }

  /**
   * Applies the sync end date from this device's license certificate. Null or
   * a past date means the subscription has lapsed.
   */
  public setSyncPaidUntil(syncUntil: string | null): void {
    const lapsed = !syncUntil || new Date(syncUntil).getTime() <= Date.now()
    if (lapsed && !this.syncLapsed.value) {
      this.disconnectAllDocuments()
      syncLog.record({ direction: 'system', level: 'warn', kind: 'sync-lapsed', message: 'Sync subscription ended; sync paused' })
    }
    this.syncLapsed.value = lapsed
    this.syncPaidUntil.value = syncUntil
  }

  public isAuthenticated(): boolean {
    return this.authToken !== null
  }

  public getWorkspaceId(): string | null {
    return this.workspaceId
  }

  public getApiUrl(): string {
    return this.apiUrl
  }

  /** Current short-lived token for sibling sync services such as checkpoints. */
  public getAuthToken(): string | null {
    return this.authToken
  }

  // ── Network & Auth Helpers ──────────────────────────────────────────────────

  private async request<T = any>(
    path: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    allowAuthRetry = true,
  ): Promise<T> {
    const requestUser = this.userId
    const hadToken = Boolean(this.authToken)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`
    }

    return withRequestDeadline(async signal => {
      const res = await fetch(`${this.apiUrl}${path}`, {
        signal,
        method,
        headers,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })

      if (requestUser !== this.userId || (hadToken && !this.authToken)) throw new Error('Cloud session changed during the request')

      // Browser timers can be suspended while a tab is backgrounded or the
      // machine sleeps. If the 15-minute access JWT expired during that gap,
      // refresh it and replay this request exactly once. The refresh promise is
      // shared so parallel tree/settings requests do not rotate independently.
      if (res.status === 401 && allowAuthRetry && this.authRefreshHandler) {
        const refreshedToken = await this.refreshAuthToken()
        throwIfAborted(signal)
        if (refreshedToken) {
          return this.request<T>(path, method, body, false)
        }
      }

      // The server says the subscription has ended (it lapsed since this
      // device's certificate was issued): stop syncing until it is renewed.
      if (res.status === 402) this.setSyncPaidUntil(null)

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string }
        const detail = err.error || res.statusText || 'Request failed'
        throw new SyncRequestError(`${detail} (${method} ${path}, HTTP ${res.status})`, res.status)
      }

      const result = (await res.json()) as T
      throwIfAborted(signal)
      return result
    })
  }


  // ── Document Sync (via Yjs + Durable Objects) ───────────────────────────────

  /**
   * Restores a page's encrypted local CRDT replica without making any network
   * request. The promise is cached by Y.Doc so revisiting a page in this app
   * session never repeats IndexedDB reads or AES-GCM decryption.
   */
  /** Only this API's media route receives the account bearer token. */
  public hasDocumentConnection(docId: string): boolean {
    return this.activeProviders.has(docId) || this.pendingConnections.has(docId)
  }

  public fetchMediaAsset(url: string, signal?: AbortSignal): Promise<Response> | null {
    let base: URL, target: URL
    try { base = new URL(this.apiUrl); target = new URL(url) } catch { return null }
    if (target.origin !== base.origin || !target.pathname.startsWith(`${base.pathname.replace(/\/$/, '')}/api/media/`)) return null
    return fetch(target.href, {
      signal, credentials: 'omit', redirect: 'error',
      headers: this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {},
    })
  }

  public prepareLocalDocument(
    docId: string,
    ydoc: Y.Doc,
  ): Promise<LocalDocumentRecovery | null> {
    const existing = this.localDocuments.get(ydoc)
    if (existing) {
      return existing.then((document) => document ? {
        hadLocalState: document.hadLocalState,
        available: true,
      } : null)
    }

    const docKey = keyManager.getDocumentKey(docId)
    if (!docKey) {
      // Never import a flat file over an encrypted CRDT merely because its key
      // is temporarily unavailable. The UI may remain locked, but identities
      // stay intact for a later offline unlock or cloud key refresh.
      return hasStoredYjsState(docId).then((hadLocalState) => (
        hadLocalState ? { hadLocalState: true, available: false } : null
      ))
    }

    const localDocument = LocalYjsDocument.open(
      docId,
      ydoc,
      docKey,
      keyManager.getDocumentEpoch(docId) ?? 1,
    ).catch((err) => {
      this.localDocuments.delete(ydoc)
      console.warn(`[SyncEngine] Failed to restore local Yjs state for ${docId}:`, err)
      return null
    })

    this.localDocuments.set(ydoc, localDocument)
    return localDocument.then(async (document) => document ? {
      hadLocalState: document.hadLocalState,
      available: true,
    } : await hasStoredYjsState(docId) ? { hadLocalState: true, available: false } : null)
  }

  public async releaseLocalDocument(ydoc: Y.Doc): Promise<void> {
    const pending = this.localDocuments.get(ydoc)
    this.localDocuments.delete(ydoc)
    const localDocument = await pending
    if (!localDocument) return
    localDocument.destroy()
    // Closing compacts the page, so whatever stays journaled is unsent.
    await localDocument.whenPersisted().catch(() => {})
    void this.refreshPendingCount()
    void this.backgroundDelivery.nudge()
  }

  /**
   * Connects a local Y.Doc to the per-document Durable Object relay with E2EE.
   */
  public async connectDocument(docId: string, ydoc: Y.Doc, options: { background?: boolean } = {}): Promise<EncryptedYjsProvider | null> {
    const lingerTimer = this.providerLingerTimers.get(docId)
    if (lingerTimer && !options.background) {
      clearTimeout(lingerTimer)
      this.providerLingerTimers.delete(docId)
    }
    const existing = this.activeProviders.get(docId)
    if (existing && !existing.isDestroyed) {
      if (!options.background) {
        this.setCurrentActiveDocId(docId)
        this.activeDocumentStatus.value = existing.status
        this.activeDocumentDeliveryStatus.value = existing.deliveryStatus
      }
      this.documentStatuses.value[docId] = existing.status
      return existing
    }

    // Deduplicate in-flight connection promises for the same docId
    const pending = this.pendingConnections.get(docId)
    if (pending) {
      return pending
    }

    if (!this.canSync.value) {
      return null
    }

    this.canceledConnections.delete(docId)

    const connectionUser = this.userId
    const connectionPromise = (async (): Promise<EncryptedYjsProvider | null> => {
      try {
        // 1. Resolve Document Encryption Key (DEK)
        let docKey = keyManager.getDocumentKey(docId)
        let isNew = this.newDocuments.has(docId)

        // A cached local key does NOT mean the server has it — only a
        // confirmed upload does. If this doc's key upload previously failed
        // (see markKeyUploadPending / retryPendingKeyUploads above), retry it
        // now rather than silently trusting the local cache, or every future
        // connection attempt for this doc keeps 403ing at the Durable Object
        // forever while looking, from the UI, like the page is just empty.
        if (docKey && (isNew || this.pendingKeyUploads.has(docId)) && this.isOnline.value && this.authToken) {
          try {
            const wrapped = await keyManager.getOrGenerateAndWrapDek(docId, keyManager.getDocumentEpoch(docId))
            const keyRes = await this.request<{
              wrappedKey?: string
              wrappedKeyNonce?: string
            }>(`/api/documents/${docId}/keys`, 'POST', {
              targetUserId: this.userId || undefined,
              wrappedKey: wrapped.ciphertext,
              wrappedKeyNonce: wrapped.iv,
              epoch: keyManager.getDocumentEpoch(docId) ?? 1,
            })
            if (keyRes?.wrappedKey && keyRes.wrappedKey !== wrapped.ciphertext) {
              docKey = await this.adoptDocumentKey(docId, {
                ciphertext: keyRes.wrappedKey,
                iv: keyRes.wrappedKeyNonce!,
              }, 1)
            }
            this.newDocuments.delete(docId)
            this.clearKeyUploadPending(docId)
          } catch (e) {
            console.warn(`[SyncEngine] Key upload retry failed for ${docId}, will retry again later:`, e)
            this.markKeyUploadPending(docId)
            if (e instanceof SyncRequestError && e.status === 409) throw e
          }
        } else {
          this.newDocuments.delete(docId)
        }

        if (!docKey && keyManager.isUnlocked() && !isNew) {
          try {
            // Try fetching wrapped DEK from server
            const keyRow = await this.request<{
              wrapped_key: string
              wrapped_key_nonce: string
              epoch: number
            }>(`/api/documents/${docId}/keys`)

            docKey = await this.adoptDocumentKey(docId, {
              ciphertext: keyRow.wrapped_key,
              iv: keyRow.wrapped_key_nonce,
            }, keyRow.epoch)
          } catch (err: any) {
            // Only generate a new DEK if the document key genuinely does not exist on the server (404)
            if (err instanceof SyncRequestError && err.status === 404 && !(await hasStoredYjsState(docId))) {
              isNew = true
              const wrapped = await keyManager.generateAndWrapDek(docId)
              docKey = keyManager.getDocumentKey(docId)!

              if (this.isOnline.value && this.authToken) {
                try {
                  const keyRes = await this.request<{
                    wrappedKey?: string
                    wrappedKeyNonce?: string
                  }>(`/api/documents/${docId}/keys`, 'POST', {
                    targetUserId: this.userId || undefined,
                    wrappedKey: wrapped.ciphertext,
                    wrappedKeyNonce: wrapped.iv,
                    epoch: 1,
                  })
                  // Another client may have won a race to create epoch 1 for this doc
                  // with a different DEK. Adopt the server's canonical key so we don't
                  // keep encrypting under a key nobody else can ever decrypt.
                  if (keyRes?.wrappedKey && keyRes.wrappedKey !== wrapped.ciphertext) {
                    docKey = await this.adoptDocumentKey(docId, {
                      ciphertext: keyRes.wrappedKey,
                      iv: keyRes.wrappedKeyNonce!,
                    }, 1)
                  }
                  this.clearKeyUploadPending(docId)
                } catch (e) {
                  console.warn('[SyncEngine] Failed to upload initial doc key to server:', e)
                  this.markKeyUploadPending(docId)
                }
              }
            } else {
              console.warn(`[SyncEngine] Key fetch failed for document ${docId}:`, err)
            }
          }
        } else if (isNew && !docKey && keyManager.isUnlocked()) {
          // Directly generate fresh DEK and upload wrapped key
          const wrapped = await keyManager.generateAndWrapDek(docId)
          docKey = keyManager.getDocumentKey(docId)!

          if (this.isOnline.value && this.authToken) {
            try {
              const keyRes = await this.request<{
                wrappedKey?: string
                wrappedKeyNonce?: string
              }>(`/api/documents/${docId}/keys`, 'POST', {
                targetUserId: this.userId || undefined,
                wrappedKey: wrapped.ciphertext,
                wrappedKeyNonce: wrapped.iv,
                epoch: 1,
              })
              // Another client may have won a race to create epoch 1 for this doc
              // with a different DEK. Adopt the server's canonical key so we don't
              // keep encrypting under a key nobody else can ever decrypt.
              if (keyRes?.wrappedKey && keyRes.wrappedKey !== wrapped.ciphertext) {
                docKey = await this.adoptDocumentKey(docId, {
                  ciphertext: keyRes.wrappedKey,
                  iv: keyRes.wrappedKeyNonce!,
                }, 1)
              }
              this.clearKeyUploadPending(docId)
            } catch (e) {
              console.warn('[SyncEngine] Failed to upload initial doc key to server:', e)
              this.markKeyUploadPending(docId)
            }
          }
        }

        if (!docKey) {
          throw new Error(`Cannot connect sync for ${docId}: Document encryption key is not available`)
        }

        if (this.pendingKeyUploads.has(docId)) throw new Error('Waiting for the document encryption key to be uploaded')

        // Local recovery is deliberately independent of the network provider.
        // App.vue normally awaits this before binding the editor; awaiting the
        // cached promise here also protects non-editor callers.
        const localRecovery = await this.prepareLocalDocument(docId, ydoc)
        const localDocument = await this.localDocuments.get(ydoc)

        // Abort if disconnectDocument was invoked while awaiting keys
        if (this.canceledConnections.has(docId) || !this.canSync.value || connectionUser !== this.userId) {
          this.canceledConnections.delete(docId)
          return null
        }

        const wsUrl = `${this.wsUrl}/api/documents/${docId}/sync`
        const provider = new EncryptedYjsProvider({
          ydoc,
          docId,
          docKey,
          wsUrl,
          authToken: () => this.authToken,
          // Exchange the JWT for a single-use ticket before each WebSocket connection,
          // so the JWT never appears in the WebSocket URL or server access logs.
          ticketUrl: `${this.apiUrl}/api/documents/${docId}/ws-ticket`,
          epoch: keyManager.getDocumentEpoch(docId) ?? 1,
          isNew,
          onAuthRefreshNeeded: () => this.refreshAuthToken(),
          onKeyRefreshNeeded: () => this.refreshDocumentKey(docId),
          onStatusChange: (status) => {
            this.logPageStatus(docId, status)
            this.documentStatuses.value[docId] = status
            if (this.currentActiveDocId === docId) {
              this.activeDocumentStatus.value = status
            }
          },
          onDeliveryStatusChange: (status) => {
            if (this.currentActiveDocId === docId) {
              this.activeDocumentDeliveryStatus.value = status
            }
          },
          onBatchAcknowledged: (updateCount) => {
            this.noteSent(updateCount)
            void this.refreshPendingCount()
            syncLog.record({ direction: 'out', kind: 'page-sent', message: 'Sent edits', targetType: 'page', targetIds: [docId], items: updateCount })
          },
          localStateRestored: localRecovery !== null,
          localDocument: localDocument || undefined,
        })

        // Double check cancellation in case it happened during provider construction
        if (this.canceledConnections.has(docId) || !this.canSync.value || connectionUser !== this.userId) {
          this.canceledConnections.delete(docId)
          provider.destroy()
          return null
        }

        this.activeProviders.set(docId, provider)
        this.watchRemoteEdits(docId, ydoc)
        return provider
      } finally {
        this.pendingConnections.delete(docId)
      }
    })()

    this.pendingConnections.set(docId, connectionPromise)
    return connectionPromise
  }

  /**
   * Fetches metadata for all snapshots of a document from Cloudflare D1,
   * decrypts labels client-side with the MEK, reconciles with local storage,
   * and prunes local versions removed by server-side retention.
   */
  public async listDocumentVersions(
    docId: string
  ): Promise<Array<{ id: string; timestamp: number; wordCount: number; label: string }>> {
    const masterKey = keyManager.getMasterKey()

    // If offline or not authenticated or key locked, return whatever is in local storage
    if (!this.authToken || !this.isOnline.value || !masterKey) return localVersionList(docId)

    try {
      const res = await this.request<{ versions: ServerVersionRow[] }>(`/api/documents/${docId}/versions`)
      if (!res || !Array.isArray(res.versions)) return localVersionList(docId)
      const serverVersions = await decodeServerVersions(res.versions, masterKey)
      // Reconcile and prune local versions deleted by server-side retention (50 limit)
      return await reconcileWithLocalVersions(docId, serverVersions)
    } catch (err) {
      console.warn(`[SyncEngine] Failed to fetch versions for ${docId}:`, err)
      return localVersionList(docId)
    }
  }

  /**
   * Fetches the encrypted snapshot content blob on-demand from R2,
   * decrypts it using the document DEK (or MEK), caches it locally,
   * and returns the plaintext string.
   */
  public async fetchSnapshotContent(
    docId: string,
    versionIdOrNumber: string | number
  ): Promise<string | null> {
    // 1. Check local cache via platform bridge first
    if (typeof window !== 'undefined' && (window as any).api?.getVersionContent) {
      const cached = await (window as any).api.getVersionContent(docId, String(versionIdOrNumber))
      if (cached && cached.trim().length > 0) {
        return cached
      }
    }

    if (!this.apiUrl) return null

    // 2. Fetch binary blob from R2 via Cloudflare Worker
    try {
      const url = `${this.apiUrl}/api/documents/${docId}/snapshot/${versionIdOrNumber}`
      const headers: Record<string, string> = {}
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`
      }

      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      })

      if (!res.ok) {
        console.warn(`[SyncEngine] Snapshot fetch returned ${res.status} for ${docId}/${versionIdOrNumber}`)
        return null
      }

      const blobBytes = new Uint8Array(await res.arrayBuffer())
      if (blobBytes.byteLength < 28) {
        console.warn('[SyncEngine] Snapshot blob too small to contain IV + auth tag')
        return null
      }

      // 3. Resolve DEK (or MEK fallback)
      let dek = keyManager.getDocumentKey(docId)
      if (!dek && keyManager.isUnlocked()) {
        try {
          const keyRow = await this.request<{
            wrapped_key: string
            wrapped_key_nonce: string
            epoch: number
          }>(`/api/documents/${docId}/keys`)
          dek = await this.adoptDocumentKey(docId, {
            ciphertext: keyRow.wrapped_key,
            iv: keyRow.wrapped_key_nonce,
          }, keyRow.epoch)
        } catch {}
      }
      if (!dek) {
        dek = keyManager.getMasterKey() || undefined
      }
      if (!dek) {
        console.warn('[SyncEngine] Cannot decrypt snapshot: key session locked or missing DEK')
        return null
      }

      // 4. Decrypt
      const content = await decryptSnapshotBlob(blobBytes, dek)

      // 5. Cache locally so subsequent loads are instant and offline-ready
      if (typeof window !== 'undefined' && (window as any).api?.cacheRemoteSnapshot) {
        await (window as any).api.cacheRemoteSnapshot(
          docId,
          String(versionIdOrNumber),
          content,
          'Snapshot',
          Date.now(),
          countWordsFromContent(content)
        ).catch(() => {})
      }

      return content
    } catch (err) {
      console.warn(`[SyncEngine] Error fetching or decrypting snapshot ${docId}/${versionIdOrNumber}:`, err)
      return null
    }
  }

  /**
   * Refreshes the Document Encryption Key (DEK) for a document from the server,
   * unwraps it with the master key, updates the key cache and any active provider.
   */
  private async adoptDocumentKey(docId: string, wrapped: import('../crypto/crypto').EncryptedBlob, epoch: number): Promise<CryptoKey> {
    const old = keyManager.getDocumentKey(docId)
    const master = keyManager.getMasterKey()
    if (old && master && await hasStoredYjsState(docId)) {
      const raw = await aesGcmDecrypt(master, wrapped)
      try {
        const candidate = await importAesKey(raw, ['encrypt', 'decrypt'], false)
        await aesGcmDecrypt(candidate, await aesGcmEncrypt(old, new Uint8Array([83, 69, 73])))
      } catch {
        throw new SyncRequestError('Cloud encryption key changed. Local writing is retained under its original key; save a recovery copy before repairing this page.', 409)
      } finally { raw.fill(0) }
    }
    return keyManager.unwrapAndCacheDek(docId, wrapped, epoch)
  }

  public async refreshDocumentKey(docId: string): Promise<CryptoKey | null> {
    if (!this.authToken || !keyManager.isUnlocked()) {
      return null
    }

    try {
      const keyRow = await this.request<{
        wrapped_key: string
        wrapped_key_nonce: string
        epoch: number
      }>(`/api/documents/${docId}/keys`)

      const docKey = await this.adoptDocumentKey(
        docId,
        {
          ciphertext: keyRow.wrapped_key,
          iv: keyRow.wrapped_key_nonce,
        },
        keyRow.epoch
      )

      console.log(`[SyncEngine] Successfully refreshed DEK for document ${docId} (epoch ${keyRow.epoch})`)

      const provider = this.activeProviders.get(docId)
      if (provider) {
        provider.setDocKey(docKey, keyRow.epoch)
      }

      return docKey
    } catch (err) {
      console.warn(`[SyncEngine] Key refresh failed for ${docId}:`, err)
      if (err instanceof SyncRequestError && err.status === 409) await db.pageDelivery.put({ docId, status: 'failed', attempts: 1, lastError: err.message, httpStatus: 409 })
      return null
    }
  }

  public setCurrentActiveDocId(docId: string | null): void {
    this.currentActiveDocId = docId
    if (docId) {
      this.activeDocumentStatus.value = this.documentStatuses.value[docId] || 'disconnected'
      this.activeDocumentDeliveryStatus.value = this.activeProviders.get(docId)?.deliveryStatus || 'synced'
    } else {
      this.activeDocumentStatus.value = 'disconnected'
      this.activeDocumentDeliveryStatus.value = 'synced'
    }
  }

  public getCurrentActiveDocId(): string | null {
    return this.currentActiveDocId
  }

  public async retryActiveDocumentKeyRefresh(): Promise<void> {
    if (!this.currentActiveDocId) return
    const provider = this.activeProviders.get(this.currentActiveDocId)
    if (provider) {
      await provider.retryKeyRefresh()
    } else {
      await this.refreshDocumentKey(this.currentActiveDocId)
    }
  }

  public async flushActiveDocument(): Promise<void> {
    if (!this.currentActiveDocId) return
    await this.activeProviders.get(this.currentActiveDocId)?.flushPendingUpdates()
  }

  private watchRemoteEdits(docId: string, ydoc: Y.Doc): void {
    this.remoteEditListeners.get(docId)?.()
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      if (origin !== 'remote') return
      syncLog.record({ direction: 'in', kind: 'page-received', message: 'Received edits', targetType: 'page', targetIds: [docId], items: 1 })
    }
    ydoc.on('update', onUpdate)
    this.remoteEditListeners.set(docId, () => ydoc.off('update', onUpdate))
  }

  /** Logs a page's connection only when it goes wrong or recovers. */
  private logPageStatus(docId: string, status: ConnectionStatus): void {
    const previous = this.loggedPageStatus.get(docId)
    this.loggedPageStatus.set(docId, status)
    if (status === previous) return
    const base = { direction: 'system' as const, kind: 'page-connection', targetType: 'page' as const, targetIds: [docId] }
    if (status === 'error') {
      syncLog.record({ ...base, level: 'warn', message: 'Lost the live connection for this page; reconnecting' })
    } else if (status === 'epoch_mismatch') {
      syncLog.record({ ...base, level: 'warn', message: 'Couldn’t decrypt changes to this page; fetching its key again' })
    } else if (status === 'connected' && (previous === 'error' || previous === 'epoch_mismatch')) {
      syncLog.record({ ...base, message: 'Live connection for this page restored' })
    }
  }

  /**
   * Disconnects and destroys active provider for a document.
   */
  public disconnectDocument(docId: string): void {
    const lingerTimer = this.providerLingerTimers.get(docId)
    if (lingerTimer) {
      clearTimeout(lingerTimer)
      this.providerLingerTimers.delete(docId)
    }
    this.canceledConnections.add(docId)
    const provider = this.activeProviders.get(docId)
    if (provider) {
      provider.destroy()
      this.activeProviders.delete(docId)
    }
    this.remoteEditListeners.get(docId)?.()
    this.remoteEditListeners.delete(docId)
    this.loggedPageStatus.delete(docId)
    delete this.documentStatuses.value[docId]
    if (this.currentActiveDocId === docId) {
      this.setCurrentActiveDocId(null)
    }
  }

  /**
   * Marks a page inactive without putting provider creation/destruction on the
   * navigation path. A short warm window makes back-and-forth switching cheap;
   * the timer still bounds open sockets and memory for long sessions.
   */
  public deactivateDocument(docId: string): void {
    if (this.currentActiveDocId === docId) this.setCurrentActiveDocId(null)
    if (this.providerLingerTimers.has(docId)) return
    const timer = setTimeout(() => {
      this.providerLingerTimers.delete(docId)
      this.disconnectDocument(docId)
      void this.backgroundDelivery.nudge()
    }, 60_000)
    this.providerLingerTimers.set(docId, timer)
  }

  /** Stops every live cloud collaboration connection without clearing local caches/data. */
  public disconnectAllDocuments(): void {
    const docIds = new Set([
      ...this.activeProviders.keys(),
      ...this.pendingConnections.keys(),
      ...this.providerLingerTimers.keys(),
    ])
    for (const docId of docIds) {
      this.disconnectDocument(docId)
    }
  }

  public async ensureWorkspaceId(): Promise<string | null> {
    if (this.workspaceId) return this.workspaceId
    if (!this.authToken) return null
    try {
      const wsList = await this.request<{ workspaces: Array<{ id: string }> }>('/api/workspaces')
      if (wsList.workspaces && wsList.workspaces.length > 0) {
        this.workspaceId = wsList.workspaces[0].id
        return this.workspaceId
      }
    } catch {}
    return null
  }

  // ── Document Tree Sync (REST + HLC LWW) ──────────────────────────────────────

  /**
   * Pushes tree mutations to the Cloudflare Worker backend (or enqueues in outbox if offline).
   */
  public async pushTreeChanges(changes: TreeChange[]): Promise<void> {
    if (!changes || changes.length === 0) return

    for (const change of changes) {
      if (!Object.prototype.hasOwnProperty.call(change, 'parentId')) {
        throw new Error(`Tree mutation for ${change.id} is missing its parentId`)
      }
    }

    const masterKey = keyManager.getMasterKey()

    // Encrypt titles, icons, and colors before sending
    const mutations = await Promise.all(changes.map((c) => encryptTreeChange(c, masterKey)))

    for (const mutation of mutations) {
      try { await this.enqueueMutation('project', 'update', mutation.id, mutation) }
      catch { /* Persistent local-save notice owns retry; callers may be synchronous UI actions. */ }
    }
  }

  /**
   * Fetches the remote tree and decrypts titles, icons, and colors with MEK.
   * With a stored cursor (and no forceFull) only rows changed after it are
   * requested. The result carries `isDelta` and `purgedIds` (pages or folders
   * permanently deleted elsewhere). The cursor is NOT stored here: callers pass
   * the result to commitTreePull() once it has been applied, so a result that
   * is dropped (an error elsewhere, the app closing) is simply fetched again.
   */
  public async pullTree(
    workspaceId?: string,
    options?: { forceFull?: boolean }
  ): Promise<Array<any>> {
    const wsId = workspaceId || (await this.ensureWorkspaceId())
    if (!wsId || !this.authToken) return []

    const cursor = options?.forceFull ? null : this.getTreeCursor(wsId)
    const url = cursor !== null
      ? `/api/workspaces/${wsId}/tree?after=${cursor}`
      : `/api/workspaces/${wsId}/tree`
    let res: { nodes: Array<any>; purged?: string[]; cursor?: number; isDelta?: boolean }
    try {
      res = await this.request(url)
    } catch (err) {
      syncLog.record({ direction: 'in', level: 'warn', kind: 'tree-pull', message: `Couldn’t check for folder and page changes: ${errorText(err)}`, httpStatus: statusOf(err) })
      throw err
    }
    // An older server ignores ?after and sends the whole tree without a cursor.
    const isDelta = cursor !== null && res.isDelta === true
    const purgedIds = Array.isArray(res.purged) ? res.purged : []

    this.lastCheckedAt.value = Date.now()

    // If delta sync returned 0 nodes, nothing changed: return empty array immediately (0 crypto ops)
    if (res.nodes.length === 0) {
      if (purgedIds.length > 0) {
        syncLog.record({ direction: 'in', kind: 'tree-pull', message: 'Received permanent deletions', targetType: 'tree', targetIds: purgedIds, items: purgedIds.length })
      } else {
        syncLog.record({ direction: 'in', kind: 'tree-check', message: 'Checked for folder and page changes; none new' })
      }
      const emptyResult: Array<any> = []
      ;(emptyResult as any).isDelta = isDelta
      ;(emptyResult as any).purgedIds = purgedIds
      ;(emptyResult as any).treeCursor = { workspaceId: wsId, cursor: res.cursor }
      return emptyResult
    }

    const masterKey = keyManager.getMasterKey()
    noteImplausibleHlcs('tree', res.nodes.filter((node) => node.hlc_timestamp && !isPlausibleHlc(node.hlc_timestamp)).map((node) => node.id))

    const decryptedNodes = await Promise.all(
      res.nodes.map(async (node) => {
        if (node.hlc_timestamp) {
          localHLC.receive(node.hlc_timestamp)
        }
        return decryptTreeNode(node, masterKey)
      })
    )

    syncLog.record({
      direction: 'in',
      kind: 'tree-pull',
      message: isDelta ? 'Received folder and page changes' : 'Received the full folder and page list',
      targetType: 'tree',
      targetIds: decryptedNodes.map((node) => node.id),
      items: decryptedNodes.length + purgedIds.length,
    })
    const unreadable = decryptedNodes.filter((node) => node.name === '[Encrypted]').map((node) => node.id)
    if (unreadable.length > 0) {
      syncLog.record({ direction: 'in', level: 'warn', kind: 'tree-undecryptable', message: 'Couldn’t decrypt the names of some folders or pages', targetType: 'tree', targetIds: unreadable, items: unreadable.length })
    }

    // Full pulls include tombstones too so a newly-connected device can show
    // the same recoverable Trash as the device where deletion happened.
    const result = decryptedNodes
    ;(result as any).isDelta = isDelta
    ;(result as any).purgedIds = purgedIds
    ;(result as any).treeCursor = { workspaceId: wsId, cursor: res.cursor }
    return result
  }

  /** Records that a pullTree() result has been applied to the local corpus. */
  public commitTreePull(result: Array<any> | null | undefined): void {
    const pulled = (result as any)?.treeCursor as { workspaceId: string; cursor?: unknown } | undefined
    if (pulled && typeof pulled.cursor === 'number') this.setTreeCursor(pulled.workspaceId, pulled.cursor)
    this.treePulled.value = true
  }

  // ── Settings Sync (REST + Per-Key LWW) ───────────────────────────────────────

  /**
   * Pushes user settings to Cloudflare Worker backend.
   */
  public async pushSettings(changes: SettingChange[]): Promise<void> {
    if (!changes || changes.length === 0) return

    const masterKey = keyManager.getMasterKey()

    const mutations = await Promise.all(changes.map((c) => encryptSettingChange(c, masterKey)))

    if (!this.isOnline.value || !this.authToken) {
      for (const m of mutations) {
        await this.enqueueMutation('settings', 'update', m.key, m)
      }
      return
    }

    try {
      await this.request('/api/settings', 'PUT', { mutations })
    } catch (err) {
      console.warn('[SyncEngine] pushSettings failed, enqueuing to outbox:', err)
      for (const m of mutations) {
        await this.enqueueMutation('settings', 'update', m.key, m)
      }
    }
  }

  /**
   * Fetches remote settings and decrypts values with MEK.
   */
  /**
   * Pulls settings (and the tool rows stored alongside them). With a stored
   * cursor only rows changed after it are requested, so a missing key means
   * "unchanged". The cursor is NOT stored here: the caller passes the result
   * to commitSettingsPull() once both the settings store and every tool have
   * applied it, because they share one cursor.
   */
  public async pullSettings(): Promise<Record<string, { value: any; hlc: number }>> {
    if (!this.authToken) return {}

    const userId = this.userId
    const cursor = userId ? getSettingsCursor(userId) : null
    let res: { settings: Array<any>; cursor?: number }
    try {
      res = await this.request(cursor !== null ? `/api/settings?after=${cursor}` : '/api/settings')
    } catch (err) {
      syncLog.record({ direction: 'in', level: 'warn', kind: 'settings-pull', message: `Couldn’t check for settings changes: ${errorText(err)}`, httpStatus: statusOf(err) })
      throw err
    }
    this.lastCheckedAt.value = Date.now()
    const masterKey = keyManager.getMasterKey()
    const result: Record<string, { value: any; hlc: number }> = {}
    let complete = !!masterKey

    const values = await Promise.all(res.settings.map(async (row) => {
      const decrypted = await decryptSettingValue(row, masterKey)
      if (decrypted.failed) complete = false
      return decrypted.value
    }))

    noteImplausibleHlcs('setting', res.settings.filter((row) => row.hlc_timestamp && !isPlausibleHlc(row.hlc_timestamp)).map((row) => row.setting_key))
    res.settings.forEach((row, index) => {
      if (row.hlc_timestamp) {
        localHLC.receive(row.hlc_timestamp)
      }
      result[row.setting_key] = {
        value: values[index],
        hlc: row.hlc_timestamp,
      }
    })

    if (res.settings.length === 0) {
      syncLog.record({ direction: 'in', kind: 'settings-check', message: 'Checked for settings changes; none new' })
    } else {
      syncLog.record({ direction: 'in', kind: 'settings-pull', message: 'Received settings changes', targetType: 'setting', items: res.settings.length })
      if (!complete) {
        syncLog.record({ direction: 'in', level: 'warn', kind: 'settings-undecryptable', message: 'Couldn’t decrypt some settings; they will be fetched again', targetType: 'setting' })
      }
    }

    // A row that could not be decrypted is skipped by its consumer, so moving
    // the cursor past it would lose it for good.
    if (complete && userId && typeof res.cursor === 'number') {
      Object.defineProperty(result, SETTINGS_PULL_CURSOR, {
        value: { userId, cursor: res.cursor },
        enumerable: false,
      })
    }
    return result
  }

  /** Records that a pullSettings() result reached the settings store and every tool. */
  public commitSettingsPull(result: Record<string, unknown> | null | undefined): void {
    const pulled = (result as any)?.[SETTINGS_PULL_CURSOR] as { userId: string; cursor: number } | undefined
    if (pulled && pulled.userId === this.userId) setSettingsCursor(pulled.userId, pulled.cursor)
  }

  // ── Offline Outbox & Reconnection ───────────────────────────────────────────

  public async refreshPendingCount(): Promise<number> {
    try {
      const [count, failed, dormantPages] = await Promise.all([
        db.outbox.count(),
        db.outbox.where('status').equals('failed').count(),
        listDormantPagesWithEdits().catch(() => [] as string[]),
      ])
      this.pendingCount.value = count
      const pageStates = await db.pageDelivery.bulkGet(dormantPages)
      const failedPages = pageStates.filter(state => state?.status === 'failed').length
      this.failedCount.value = failed + failedPages
      this.pendingPageCount.value = dormantPages.length
      this.trackWaiting(count - failed + dormantPages.length - failedPages)
      return count
    } catch {
      return 0
    }
  }

  /** Follows the size of the backlog, and keeps the clock ticking while there is one. */
  private trackWaiting(waiting: number): void {
    const now = Date.now()
    if (waiting > 0) {
      if (this.waitingSince.value === null) {
        this.waitingSince.value = now
        this.backlogTotal.value = waiting
        this.sendTimes.value = []
      } else if (waiting > this.backlogTotal.value) {
        this.backlogTotal.value = waiting
      }
      if (!this.clockTimer && typeof setInterval !== 'undefined') {
        this.clock.value = now
        this.clockTimer = setInterval(() => { this.clock.value = Date.now() }, 1_000)
      }
    } else {
      this.waitingSince.value = null
      this.backlogTotal.value = 0
      this.serverUnreachable.value = false
      if (this.clockTimer) {
        clearInterval(this.clockTimer)
        this.clockTimer = null
      }
      this.clock.value = now
    }
  }

  private noteSent(items: number): void {
    const now = Date.now()
    this.lastSentAt.value = now
    this.sendTimes.value = [
      ...this.sendTimes.value.filter((send) => now - send.at <= RATE_WINDOW_MS),
      { at: now, items },
    ]
  }

  /**
   * Queues a change for upload. A change that carries the whole current state
   * of its target replaces any older queued copy (see supersedes()), so the
   * queue holds one item per page, row or setting rather than one per save.
   */
  private readonly latestLocalIntent = new Map<string, { id: string; hlc: number }>()

  public async enqueueMutation(
    type: StoredMutation['type'],
    action: StoredMutation['action'],
    targetId: string,
    payload: any,
  ): Promise<string> {
    const id = `mut-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const hlcTs = localHLC.now()
    const now = Date.now()
    const mutation: StoredMutation = {
      id,
      type,
      action,
      targetId,
      payload: typeof payload === 'string' ? payload : JSON.stringify(payload),
      hlc: String(hlcTs),
      timestamp: now,
      firstQueuedAt: now,
      status: 'pending',
    }

    this.latestLocalIntent.set(`${type}:${targetId}`, { id, hlc: Number(mutation.hlc) })
    try { journalMutation(mutation) } catch { /* Primary IndexedDB may still succeed. */ }
    await this.persistMutation(mutation)
    if (this.canSync.value && !this.isSyncing.value) void this.flushOutbox().catch(() => {})
    return id
  }

  private async persistMutation(mutation: StoredMutation): Promise<void> {
    const target = `${mutation.type}:${mutation.targetId}`
    const latest = this.latestLocalIntent.get(target)
    if (!latest || Number(mutation.hlc) >= latest.hlc) this.latestLocalIntent.set(target, { id: mutation.id, hlc: Number(mutation.hlc) })
    await serializeLocalWrite(`outbox:${target}`, async () => {
      if (this.latestLocalIntent.get(target)?.id !== mutation.id) return
      try {
        await db.transaction('rw', db.outbox, async () => {
          if (await db.outbox.get(mutation.id)) return // Idempotent journal replay keeps delivery state.
          const queued = await db.outbox.where('targetId').equals(mutation.targetId).toArray()
          if (mutation.type === 'document' && mutation.action === 'delete') {
            queued.push(...await db.outbox.where('type').equals('snapshot').toArray())
          }
          if (queued.some((item) => isStale(mutation, item))) return

          const replaced = queued.filter((item) => supersedes(mutation, item))
          if (replaced.length === 0) {
            await db.outbox.add(mutation)
            return
          }
          await db.outbox.bulkDelete(replaced.map((item) => item.id))
          await db.outbox.add(mutation)
        })
        try { clearJournalMutation(mutation) } catch {}
        await this.refreshPendingCount().catch(() => {})
        this.syncChannel?.postMessage({ type: 'outbox-updated' })
      } catch (e) {
        localSaveFailed(`outbox:${mutation.type}:${mutation.targetId}`, e, () => this.persistMutation(mutation))
        console.warn('[SyncEngine] Failed to add mutation to outbox:', e)
        throw e
      }

      localSaveSucceeded(`outbox:${mutation.type}:${mutation.targetId}`)
    })
  }

  public async recoverPendingMutations(): Promise<void> {
    let records: ReturnType<typeof readJournalMutations>
    try { records = readJournalMutations() } catch { return }
    for (const { mutation, document } of records) {
      if (isRetiredPageText(mutation)) {
        // Its upload is retired, but a page record saved with it (older web
        // builds) is this device's copy of the page: keep that.
        try {
          if (document) await db.documents.put(document)
          clearJournalMutation(mutation)
        } catch { /* Kept for the next launch. */ }
        continue
      }
      try { await this.persistMutation(mutation) } catch { /* Notice remains until retry succeeds. */ }
    }
  }

  /**
   * Applies the enqueueMutation() merge rules to a queue built before they
   * existed, and drops page text items left by older builds.
   */
  public async compactOutbox(): Promise<number> {
    try {
      return await db.transaction('rw', db.outbox, async () => {
        const all = await db.outbox.orderBy('timestamp').toArray()
        const kept: StoredMutation[] = []
        const removed = new Set<string>()
        for (const item of all) {
          if (isRetiredPageText(item) || kept.some((earlier) => !removed.has(earlier.id) && isStale(item, earlier))) {
            removed.add(item.id)
            continue
          }
          const replaced = kept.filter((earlier) => !removed.has(earlier.id) && supersedes(item, earlier))
          for (const earlier of replaced) removed.add(earlier.id)
          kept.push(item)
        }
        if (removed.size > 0) await db.outbox.bulkDelete([...removed])
        return removed.size
      })
    } catch (e) {
      console.warn('[SyncEngine] Could not compact the outbox:', e)
      return 0
    }
  }

  // ── Sync hub ───────────────────────────────────────────────────────────────

  /** Everything not yet accepted by the server, oldest first. */
  public async listOutbox(): Promise<StoredMutation[]> {
    return db.outbox.orderBy('timestamp').toArray()
  }

  private async sendQueuedPage(docId: string, signal: AbortSignal): Promise<void> {
    throwIfAborted(signal)
    const rows = await db.outbox.where('type').equals('project').toArray()
    if (rows.some(row => row.targetId === docId)) throw new Error('Waiting for this page’s tree row to be uploaded')
    const status = (await this.checkDocumentStatus([docId])).get(docId)
    if (!status || status.state !== 'active') throw new SyncRequestError(`Cloud page is ${status?.state || 'missing'}. Restore the page or save a copy of its retained local edits.`, status?.state === 'missing' ? 404 : 410)
    throwIfAborted(signal)
    const ydoc = getPageYdoc(docId)
    const existing = this.hasDocumentConnection(docId)
    const cancel = () => { if (!existing && this.currentActiveDocId !== docId) this.disconnectDocument(docId) }
    signal.addEventListener('abort', cancel, { once: true })
    let provider: EncryptedYjsProvider | null = null
    try {
      provider = await this.connectDocument(docId, ydoc, { background: true })
      throwIfAborted(signal)
      if (!provider) throw new Error('Cloud connection is unavailable')
      const key = keyManager.getDocumentKey(docId)
      const local = await this.localDocuments.get(ydoc)
      try {
        if (!key) throw new Error('Missing document key')
        // Validate one snapshot; acknowledgments may prune rows during decryption.
        const recovered = await loadLocalYjsOutbox(docId, key, true)
        local?.reconcilePendingUpdates(recovered)
      } catch {
        throw new SyncRequestError('Some local edits cannot be decrypted. Keep your writing and export a recovery copy before repairing its key.', 409)
      }
      await withRequestDeadline(() => provider!.waitForInitialSync(), { signal, timeoutMs: 30_000 })
      await provider.waitForDelivery(signal)
      await local?.persistCacheNow()
      // Closing a background connection must not strand edits made during delivery.
      if (local?.pendingUpdates.length) throw new Error('More edits were queued during delivery')
    } catch (cause) {
      const failure = await db.pageDelivery.get(docId)
      if (failure?.httpStatus === 409) throw new SyncRequestError(failure.lastError || 'Document key recovery required', 409)
      throw cause
    } finally {
      signal.removeEventListener('abort', cancel)
      cancel()
    }
  }

  public retryQueuedPages(docId?: string): Promise<void> { return this.backgroundDelivery.retry(docId) }

  /** Journaled edits, including background pages and their delivery diagnostics. */
  public async listPagesWithUnsentEdits(): Promise<Array<{ docId: string; edits: number; since: number; status?: 'pending' | 'failed'; attempts?: number; nextAttemptAt?: number; lastError?: string; httpStatus?: number }>> {
    const pages = await summarizeDormantPageEdits()
    return Promise.all(pages.map(async page => ({ ...page, ...(await db.pageDelivery.get(page.docId)) })))
  }

  /**
   * Removes everything this device holds for a page's live text: its open
   * connection, its local document and its journaled edits. Called whenever a
   * page stops existing here (deleted for good, a blank page discarded, purged
   * from another device), so its edits are never left waiting for a page that
   * can no longer be opened.
   */
  public async forgetLocalPage(docId: string): Promise<void> {
    this.disconnectDocument(docId)
    this.newDocuments.delete(docId)
    this.clearKeyUploadPending(docId)
    await LocalYjsDocument.closeAll(docId)
    await clearLocalYjsState(docId)
    await db.pageDelivery.delete(docId)
    await this.refreshPendingCount()
  }

  /** A page's text as this device last had it, rebuilt from its local state. */
  public async readLocalPageText(docId: string): Promise<string | null> {
    const key = keyManager.getDocumentKey(docId)
    if (!key) return null
    const ydoc = new Y.Doc()
    try {
      if (!(await readLocalYjsState(docId, ydoc, key))) return null
      return tiptapToPlainText(yXmlFragmentToProsemirrorJSON(ydoc.getXmlFragment('default')))
    } finally {
      ydoc.destroy()
    }
  }

  /** Tries these items now, whatever their state. */
  public async retryItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await db.outbox.where('id').anyOf(ids).modify((item) => {
      if (item.status === 'failed') item.attempts = 0
      item.status = 'pending'
      delete item.nextAttemptAt
    })
    this.blockedPasses = 0
    this.serverUnreachable.value = false
    await this.refreshPendingCount()
    await this.flushOutbox()
  }

  /** Gives up on uploading these items. Local pages are not touched. */
  public async discardItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    const items = (await db.outbox.bulkGet(ids)).filter((item): item is StoredMutation => !!item)
    await db.outbox.bulkDelete(ids)
    for (const item of items) {
      const what = describeMutation(item)
      syncLog.record({ direction: 'out', level: 'warn', kind: 'outbox-discarded', message: `${what.label} discarded by hand`, targetType: what.targetType, targetIds: [what.targetId] })
    }
    this.syncChannel?.postMessage({ type: 'outbox-updated' })
    await this.refreshPendingCount()
  }

  /** Asks the server what it holds for these pages (at most 100 per request). */
  public async checkDocumentStatus(ids: string[]): Promise<Map<string, DocumentServerStatus>> {
    const result = new Map<string, DocumentServerStatus>()
    const unique = [...new Set(ids)]
    const wsId = await this.ensureWorkspaceId()
    if (!wsId || unique.length === 0) return result
    for (let i = 0; i < unique.length; i += 100) {
      const res = await this.request<{ documents: DocumentServerStatus[] }>(
        `/api/workspaces/${wsId}/document-status`,
        'POST',
        { ids: unique.slice(i, i + 100) },
      )
      for (const doc of res.documents) result.set(doc.id, doc)
    }
    return result
  }

  /**
   * Gives a page back to the cloud after its row was lost there (permanently
   * deleted from another device, or never uploaded). The caller re-sends the
   * page's row first; this re-sends its key, then everything queued for it.
   * Its live text follows on the next connection, which sends the full page
   * to a cloud log it has not seen.
   */
  public async reuploadPage(docId: string): Promise<void> {
    if (keyManager.getDocumentKey(docId)) this.markKeyUploadPending(docId)
    await this.retryPendingKeyUploads()
    const items = await db.outbox.toArray()
    const ids = items
      .filter((item) => describeMutation(item).targetId === docId && item.type !== 'project')
      .map((item) => item.id)
    syncLog.record({ direction: 'out', kind: 'page-reupload', message: 'Page uploaded again by hand', targetType: 'page', targetIds: [docId] })
    await this.retryItems(ids)
  }

  /**
   * A flush the user asked for: everything waiting is tried now, including
   * items that failed earlier, since the user may have fixed the cause.
   */
  public async retryNow(): Promise<void> {
    try {
      await db.outbox.toCollection().modify((item) => {
        if (item.status === 'failed') item.attempts = 0
        item.status = 'pending'
        delete item.nextAttemptAt
      })
    } catch (e) {
      console.warn('[SyncEngine] Could not reset outbox retry state:', e)
    }
    this.blockedPasses = 0
    this.serverUnreachable.value = false
    syncLog.record({ direction: 'system', kind: 'retry-now', message: 'Sync retried by hand' })
    await this.refreshPendingCount()
    await this.flushOutbox()
    await this.backgroundDelivery.retry()
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this.nextRetryAt.value = null
  }

  /**
   * Keeps trying while anything is waiting: at the earliest item's retry time,
   * or, while the server cannot be reached, on a growing delay. Without this a
   * single failed pass left the queue idle until something else woke it.
   */
  private async scheduleRetry(): Promise<void> {
    this.clearRetryTimer()
    if (!this.canSync.value) return
    let waiting: StoredMutation[]
    try {
      waiting = await db.outbox.where('status').equals('pending').toArray()
      const pendingRows = new Set((await db.outbox.where('type').equals('project').toArray()).map(item => item.targetId))
      waiting = waiting.filter(item => {
        if (item.type === 'snapshot') {
          try { return !pendingRows.has(JSON.parse(item.payload).documentId) } catch { return true }
        }
        return true
      })
    } catch {
      return
    }
    if (waiting.length === 0) {
      this.blockedPasses = 0
      return
    }
    const now = Date.now()
    let at = Math.min(...waiting.map((item) => item.nextAttemptAt ?? now))
    if (this.blockedPasses > 0) {
      const index = Math.min(this.blockedPasses, BLOCKED_RETRY_DELAYS_MS.length) - 1
      at = Math.max(at, now + BLOCKED_RETRY_DELAYS_MS[index])
    }
    this.clearRetryTimer()
    const delay = Math.max(at - now, 1_000)
    this.nextRetryAt.value = now + delay
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.nextRetryAt.value = null
      void this.flushOutbox()
    }, delay)
  }

  /**
   * Sends a batch; if the server refuses it in a way one bad item could cause,
   * sends each half separately, so a single bad item cannot hold back the rest.
   */
  private async sendInHalves<T>(
    items: T[],
    send: (batch: T[]) => Promise<unknown>,
    onSent: (batch: T[]) => void,
    onFailed: (item: T, err: unknown) => void,
    stop: () => boolean,
  ): Promise<void> {
    if (items.length === 0 || stop()) return
    try {
      await send(items)
      onSent(items)
    } catch (err) {
      if (items.length > 1 && worthSplitting(err)) {
        const mid = Math.ceil(items.length / 2)
        await this.sendInHalves(items.slice(0, mid), send, onSent, onFailed, stop)
        await this.sendInHalves(items.slice(mid), send, onSent, onFailed, stop)
        return
      }
      for (const item of items) onFailed(item, err)
    }
  }

  private async recordFailures(failures: Array<{ id: string; err: unknown }>): Promise<void> {
    const now = Date.now()
    const updated: StoredMutation[] = []
    try {
      await db.transaction('rw', db.outbox, async () => {
        for (const { id, err } of failures) {
          // The item may have been replaced by a newer copy meanwhile.
          const item = await db.outbox.get(id)
          if (!item) continue
          const next = afterFailure(item, err, now)
          await db.outbox.update(id, next)
          updated.push({ ...item, ...next })
        }
      })
    } catch (e) {
      console.warn('[SyncEngine] Could not record outbox failures:', e)
    }
    for (const item of updated) {
      const what = describeMutation(item)
      syncLog.record({
        direction: 'out',
        level: item.status === 'failed' ? 'error' : 'warn',
        kind: item.status === 'failed' ? 'outbox-refused' : 'outbox-retry',
        message: item.status === 'failed'
          ? `${what.label} set aside: ${item.lastError}`
          : `${what.label} failed; will retry: ${item.lastError}`,
        targetType: what.targetType,
        targetIds: [what.targetId],
        httpStatus: item.httpStatus,
      })
    }
  }

  public async flushOutbox(): Promise<void> {
    await this.recoverPendingMutations()
    if (!this.canSync.value) return

    // Deduplicate in-flight flush calls within the same tab
    if (this.flushPromise) {
      return this.flushPromise
    }

    this.clearRetryTimer()

    this.flushPromise = (async () => {
      const runFlush = async () => {
        if (!this.canSync.value) return

        /** Set when the server cannot be reached at all; ends this pass. */
        let blocked = false
        let madeProgress = false
        let dependencyBlocked = false

        try {
          this.isSyncing.value = true

          // Loop until nothing is due or the server cannot be reached
          while (this.canSync.value && !blocked) {
            const now = Date.now()
            const pendingMutations = (await db.outbox
              .where('status')
              .equals('pending')
              .sortBy('timestamp'))
              .filter((mut) => isDue(mut, now))

            if (pendingMutations.length === 0) break

            const wsId = await this.ensureWorkspaceId()
            const masterKey = keyManager.getMasterKey()

            const {
              tree: treeMutations,
              documents: docMutations,
              snapshots: snapshotMutations,
              settings: settingsMutations,
              dropped,
            } = groupOutboxItems(pendingMutations)

            const processedIds: string[] = [...dropped]
            const failures: Array<{ id: string; err: unknown }> = []
            const fail = (id: string, err: unknown) => {
              if (classifyFailure(err) === 'blocked') {
                if (!blocked) {
                  console.warn('[SyncEngine] Server unreachable, will retry outbox later:', err)
                  syncLog.record({ direction: 'out', level: 'warn', kind: 'outbox-unreachable', message: `Couldn’t reach the server: ${errorText(err)}`, httpStatus: statusOf(err) })
                }
                blocked = true
              } else {
                console.warn(`[SyncEngine] Outbox item ${id} failed:`, err)
                failures.push({ id, err })
              }
            }
            const isBlocked = () => blocked

            // 1. Process tree mutations FIRST so document rows exist in D1
            if (treeMutations.length > 0 && wsId) {
              await this.sendInHalves(
                treeMutations,
                (batch) => this.request(`/api/workspaces/${wsId}/tree`, 'PUT', {
                  mutations: batch.map((m) => m.payload),
                }),
                (batch) => processedIds.push(...batch.map((m) => m.id)),
                (m, err) => fail(m.id, err),
                isBlocked,
              )
            }

            // Never spend version attempts while its queued tree row is
            // failed, backing off, or was added during this flush.
            const awaitingRows = new Set((await db.outbox.where('type').equals('project').toArray())
              .filter(item => !processedIds.includes(item.id)).map(item => item.targetId))

            // 2. Process page deletions. Page text travels through each page's
            // edit log; a text item from an older build is dropped unsent.
            if (masterKey && keyManager.isUnlocked()) {
              for (const docMut of docMutations) {
                if (blocked) break
                if (docMut.action !== 'delete') {
                  processedIds.push(docMut.id)
                  continue
                }
                try {
                  await this.request(`/api/documents/${docMut.targetId}`, 'DELETE')
                  processedIds.push(docMut.id)
                } catch (docErr) {
                  fail(docMut.id, docErr)
                }
              }
            }

            // 3. Process snapshot mutations
            if (masterKey && keyManager.isUnlocked()) {
              for (const snapMut of snapshotMutations) {
                if (blocked) break
                if (awaitingRows.has(snapMut.payload.documentId)) continue
                try {
                  const encLabel = await aesGcmEncrypt(
                    masterKey,
                    new TextEncoder().encode(snapMut.payload.label || 'Snapshot')
                  )
                  const wordCount = snapMut.payload.wordCount ?? countWordsFromContent(snapMut.payload.content)
                  const vRes = await this.request<{ id: string; versionNumber: number }>(
                    `/api/documents/${snapMut.payload.documentId}/versions`,
                    'POST',
                    {
                      id: snapMut.payload.versionId || snapMut.targetId,
                      wordCount,
                      createdAt: typeof snapMut.payload.createdAt === 'number' ? snapMut.payload.createdAt : undefined,
                      labelEncrypted: encLabel.ciphertext,
                      labelNonce: encLabel.iv,
                      epoch: 1,
                    }
                  )

                  const dek = keyManager.getDocumentKey(snapMut.payload.documentId) || masterKey
                  const packed = await packSnapshotBlob(snapMut.payload.content || '', dek)

                  // Address the version by id: on a retry the server keeps the
                  // existing row but reports a fresh (wrong) version number.
                  const versionRef = vRes.id && !/^\d+$/.test(vRes.id) ? vRes.id : String(vRes.versionNumber)
                  const blobPath = `/api/documents/${snapMut.payload.documentId}/snapshot/${encodeURIComponent(versionRef)}`
                  const blobRes = await fetch(`${this.apiUrl}${blobPath}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/octet-stream',
                      ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
                    },
                    body: packed,
                  })
                  if (!blobRes.ok) {
                    throw new SyncRequestError(`Snapshot upload failed (PUT ${blobPath}, HTTP ${blobRes.status})`, blobRes.status)
                  }

                  processedIds.push(snapMut.id)
                } catch (snapErr) {
                  fail(snapMut.id, snapErr)
                }
              }
            }

            // 4. Process settings mutations
            if (settingsMutations.length > 0) {
              await this.sendInHalves(
                settingsMutations,
                (batch) => this.request('/api/settings', 'PUT', {
                  mutations: batch.map((s) => s.payload),
                }),
                (batch) => processedIds.push(...batch.map((s) => s.id)),
                (s, err) => fail(s.id, err),
                isBlocked,
              )
            }

            if (processedIds.length > 0) {
              madeProgress = true
              await db.outbox.bulkDelete(processedIds)
              this.noteSent(processedIds.length)
              logSent(pendingMutations, processedIds)
            }
            if (failures.length > 0) await this.recordFailures(failures)
            if (processedIds.length > 0 || failures.length > 0) {
              this.syncChannel?.postMessage({ type: 'outbox-updated' })
            }

            await this.refreshPendingCount()

            // Nothing sent and nothing refused: whatever is due could not be
            // attempted (e.g. no workspace yet). Back off as if unreachable.
            if (processedIds.length === 0 && failures.length === 0) {
              dependencyBlocked = Boolean(wsId) && awaitingRows.size > 0 && treeMutations.length === 0
              blocked = true
            }
          }
        } catch (err) {
          blocked = true
          console.warn('[SyncEngine] Flush outbox failed, will retry:', err)
          syncLog.record({ direction: 'out', level: 'warn', kind: 'outbox-unreachable', message: `Sending stopped: ${errorText(err)}` })
        } finally {
          if (madeProgress) this.blockedPasses = 0
          if (blocked && !dependencyBlocked) this.blockedPasses++
          this.serverUnreachable.value = this.blockedPasses > 0
          this.isSyncing.value = false
          await this.refreshPendingCount()
        }
      }

      // Coordinate cross-tab execution using Web Locks API
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        try {
          await navigator.locks.request('superelectric-ink-outbox-flush', { ifAvailable: true }, async lock => {
            if (lock) return runFlush()
            // Another window may be suspended while holding the lock. Do not
            // leave flushPromise (and every background page) waiting on it.
            this.blockedPasses++
            syncLog.record({ direction: 'out', level: 'warn', kind: 'outbox-lock-busy', message: 'Another app window is sending the queue; this window will retry.' })
          })
        } catch (lockErr) {
          console.warn('[SyncEngine] Web Lock acquisition failed, falling back to direct execution:', lockErr)
          await runFlush()
        }
      } else {
        await runFlush()
      }
    })().finally(() => {
      this.flushPromise = null
      // Awaited so a caller of flushOutbox() sees when the next try is due.
      void this.backgroundDelivery.nudge()
      return this.scheduleRetry()
    })

    return this.flushPromise
  }
}

function readSyncStopped(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(SYNC_STOPPED_KEY) === '1'
  } catch {
    return false
  }
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

function statusOf(err: unknown): number | undefined {
  return err instanceof SyncRequestError ? err.status : undefined
}

/** Carries the retry state of the queued copies a new copy replaces. */
export const syncEngine = new SyncEngine()

export function useSyncStatus() {
  return {
    isOnline: syncEngine.isOnline,
    isSyncing: syncEngine.isSyncing,
    pendingCount: syncEngine.pendingCount,
    failedCount: syncEngine.failedCount,
    pendingPageCount: syncEngine.pendingPageCount,
    syncStatus: syncEngine.syncStatus,
    syncSummary: syncEngine.syncSummary,
    lastSentAt: syncEngine.lastSentAt,
    lastCheckedAt: syncEngine.lastCheckedAt,
    ratePerMinute: syncEngine.ratePerMinute,
    nextRetryAt: syncEngine.nextRetryAt,
    documentStatus: syncEngine.activeDocumentStatus,
    deliveryStatus: syncEngine.activeDocumentDeliveryStatus,
    documentStatuses: syncEngine.documentStatuses,
    /** Tries everything waiting now, including items the server refused. */
    flushOutbox: async () => {
      await Promise.all([syncEngine.retryNow(), syncEngine.flushActiveDocument()])
    },
    /** Sends what is due now; refused items stay set aside for the hub. */
    nudge: async () => {
      await Promise.all([syncEngine.flushOutbox(), syncEngine.flushActiveDocument()])
    },
    refreshDocumentKey: (docId?: string) => {
      const id = docId || syncEngine.getCurrentActiveDocId()
      return id ? syncEngine.refreshDocumentKey(id) : Promise.resolve(null)
    },
    retryActiveDocumentKeyRefresh: () => syncEngine.retryActiveDocumentKeyRefresh(),
  }
}
